import { useState, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Badge, Tooltip } from '@mui/material';
import {
  UnfoldMore,
  UnfoldLess,
  AutoAwesome,
  FactCheck,
  Publish,
  Refresh,
  FilterList,
  FileDownload,
} from "@mui/icons-material";
import useAuthCheck from 'hooks/useAuthCheck';
import useHoldRepeat from 'hooks/useHoldRepeat';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import StyledButton from 'components/StyledButton';
import GanttRoot from './GanttRoot';
import EmployeeFilterDialog from 'components/EmployeeFilterDialog';
import PublishScheduleDialog from './PublishScheduleDialog';
import ViolationCheckDialog from './ViolationCheckDialog';


const EMPTY_FILTER = {
  employeeCode: '',
  employeeName: '',
  genders: [],
  workingModes: [],
  ranks: [],
  homebases: [],
  departments: [],
  divisions: [],
  isActive: '',
};

const Timekeeping = () => {
  useAuthCheck();

  const scrollRef       = useRef(null);
  const ganttRefreshRef = useRef(null);
  const ganttExportRef  = useRef(null);
  const [rowHeight, setRowHeight]               = useState(40);
  const [cellWidth, setCellWidth]               = useState(8);
  const [autoAssignmentOpen,   setAutoAssignmentOpen]   = useState(false);
  const [violationCheckOpen,   setViolationCheckOpen]   = useState(false);
  const [filterOpen, setFilterOpen]             = useState(false);
  const [employeeFilter, setEmployeeFilter]     = useState(EMPTY_FILTER);
  const [allEmployees, setAllEmployees]         = useState([]);
  const [publishOpen, setPublishOpen] = useState(false);

  const user = useSelector((state) => state.user); 
  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );  
  const canCreate = hasRight(permissionMap, "Timekeeping", "create", false);
  const canEdit = hasRight(permissionMap, "Timekeeping", "update", false);
  const canDelete = hasRight(permissionMap, "Timekeeping", "delete", false);
  const canPublish = hasRight(permissionMap, "Timekeeping", "publish", false);        
  const canAutoAssignment = hasRight(permissionMap, "Timekeeping", "autoassignment", false);
  
  const { startDate, endDate } = useDateRangeContext();
  const safeCellWidth = Math.max(1, cellWidth);

  /* ---------- HOLD-REPEAT CONTROLS ---------- */
  const decRowHeight = useCallback(() => setRowHeight(prev => Math.max(1, prev - 1)), []);
  const incRowHeight = useCallback(() => setRowHeight(prev => prev + 1), []);
  const decCellWidth = useCallback(() => setCellWidth(prev => Math.max(1, prev - 1)), []);
  const incCellWidth = useCallback(() => setCellWidth(prev => prev + 1), []);

  const holdDecRowHeight = useHoldRepeat(decRowHeight);
  const holdIncRowHeight = useHoldRepeat(incRowHeight);
  const holdDecCellWidth = useHoldRepeat(decCellWidth);
  const holdIncCellWidth = useHoldRepeat(incCellWidth);

  /* ---------- FILTER ---------- */
  const handleEmployeesLoaded = useCallback((rawEmployees) => {
    setAllEmployees(rawEmployees);
  }, []);

  const filterOptions = useMemo(() => ({
    genders:      [...new Set(allEmployees.map(e => e.gender).filter(Boolean))].sort(),
    workingModes: [...new Set(allEmployees.map(e => e.workingMode).filter(Boolean))].sort(),
    ranks:        [...new Set(allEmployees.map(e => e.rank).filter(Boolean))].sort(),
    homebases:    [...new Set(allEmployees.map(e => e.homeBase).filter(Boolean))].sort(),
    departments:  [...new Set(allEmployees.map(e => e.department).filter(Boolean))].sort(),
    divisions:    [...new Set(allEmployees.map(e => e.division).filter(Boolean))].sort(),
  }), [allEmployees]);

  // IDs of employees that pass the current filter - undefined means "all"
  const filteredEmployeeIds = useMemo(() => {
    const {
      employeeCode,
      employeeName,
      genders,
      workingModes,
      ranks,
      homebases,
      divisions,
      departments,
      isActive,
    } = employeeFilter;

    const codeQ = employeeCode.trim().toLowerCase();
    const nameQ = employeeName.trim().toLowerCase();
    const hasAny = codeQ || nameQ || genders.length || workingModes.length ||
      ranks.length || homebases.length || departments.length || divisions.length || isActive;

    if (!hasAny) return undefined;

    return allEmployees
      .filter(e => {
        if (codeQ                && !e.employeeCode?.toLowerCase().includes(codeQ)) return false;
        if (nameQ                && !e.employeeName?.toLowerCase().includes(nameQ)) return false;
        if (genders.length       && !genders.includes(e.gender))                    return false;
        if (workingModes.length  && !workingModes.includes(e.workingMode))          return false;
        if (ranks.length         && !ranks.includes(e.rank))                        return false;
        if (homebases.length     && !homebases.includes(e.homeBase))                return false;
        if (departments.length   && !departments.includes(e.department))            return false;
        if (divisions.length     && !divisions.includes(e.division))                return false;
        if (isActive === 'true'  && !e.isActive)                                    return false;
        if (isActive === 'false' && e.isActive)                                     return false;
        return true;
      })
      .map(e => String(e._id));
  }, [allEmployees, employeeFilter]);

  const activeFilterCount = useMemo(() => {
    const f = employeeFilter;
    return (f.employeeCode.trim()  ? 1 : 0) +
           (f.employeeName.trim()  ? 1 : 0) +
           (f.genders.length      > 0 ? 1 : 0) +
           (f.workingModes.length > 0 ? 1 : 0) +
           (f.ranks.length        > 0 ? 1 : 0) +
           (f.homebases.length    > 0 ? 1 : 0) +
           (f.departments.length  > 0 ? 1 : 0) +
           (f.divisions.length    > 0 ? 1 : 0) +
           (f.isActive            ? 1 : 0);
  }, [employeeFilter]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Timekeeping"
          subtitle={`List of Assignments`}
        />

        <Box display="flex" alignItems="center" gap={1}>
          <StyledButton
            {...holdDecRowHeight}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          >
            <UnfoldLess />
          </StyledButton>
          <StyledButton
            {...holdIncRowHeight}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          >
            <UnfoldMore />
          </StyledButton>
          <StyledButton
            {...holdDecCellWidth}
            sx={{
              transform: 'rotate(90deg)',
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            <UnfoldLess />
          </StyledButton>
          <StyledButton
            {...holdIncCellWidth}
            sx={{
              transform: 'rotate(90deg)',
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            <UnfoldMore />
          </StyledButton>

          <Tooltip title="Auto Assignment" arrow>
            <span>
              <StyledButton onClick={() => setAutoAssignmentOpen(true)} disabled={!canAutoAssignment}>
                <AutoAwesome />
              </StyledButton>
            </span>
          </Tooltip>

          <Tooltip title="Check Violations" arrow>
            <span>
              <StyledButton onClick={() => setViolationCheckOpen(true)}>
                <FactCheck />
              </StyledButton>
            </span>
          </Tooltip>

          <Tooltip title="Publish Schedule" arrow>
            <span>
              <StyledButton onClick={() => setPublishOpen(true)} disabled={!canPublish}>
                <Publish />
              </StyledButton>
            </span>
          </Tooltip>

          <Tooltip title="Export to Excel" arrow>
            <span>
              <StyledButton onClick={() => ganttExportRef.current?.()}>
                <FileDownload />
              </StyledButton>
            </span>
          </Tooltip>

          <Tooltip title="Refresh" arrow>
            <span>
              <StyledButton onClick={() => ganttRefreshRef.current?.()}>
                <Refresh />
              </StyledButton>
            </span>
          </Tooltip>
          
          <Tooltip title="Filter" arrow>
            <span>
              <StyledButton onClick={() => setFilterOpen(true)}>
                <Badge 
                  badgeContent={activeFilterCount} 
                  color="primary" 
                  invisible={!activeFilterCount}
                >
                  <FilterList />
                </Badge>
              </StyledButton>
            </span>
          </Tooltip>

        </Box>
      </FlexBetween>

      <Box 
        display="grid" 
        gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
        gap={2}
      >
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}
          sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
          <GanttRoot
            rowHeight={rowHeight}
            cellWidth={safeCellWidth}
            autoAssignmentOpen={autoAssignmentOpen}
            onAutoAssignmentClose={() => setAutoAssignmentOpen(false)}
            refreshRef={ganttRefreshRef}
            exportRef={ganttExportRef}
            employeeFilter={employeeFilter}
            onEmployeesLoaded={handleEmployeesLoaded}
            filteredEmployeeIds={filteredEmployeeIds}
            canCreate={canCreate}
            canEdit={canEdit}
            canDelete={canDelete}
            canPublish={canPublish}
          />

          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      </Box>

      <EmployeeFilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        options={filterOptions}
        value={employeeFilter}
        onChange={setEmployeeFilter}
      />

      <PublishScheduleDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        allEmployees={allEmployees}
        defaultStartDate={startDate}
        defaultEndDate={endDate}
        onPublished={() => ganttRefreshRef.current?.()}
        canPublish={canPublish}
      />

      <ViolationCheckDialog
        open={violationCheckOpen}
        onClose={() => setViolationCheckOpen(false)}
        startDate={startDate}
        endDate={endDate}
        employeeIds={filteredEmployeeIds}
      />
    </Box>
  );
};


export default Timekeeping;
